import Base from "+/Engine/Base";
import Routine from "+/Engine/Routine";
import { effectScope } from "vue";

export default class Event extends Base {
  static #validParamTypes = [
    "string",
    "entity",
    "oneEntity",
    "manyEntities",
    "number",
    "boolean",
    "story",
    "image",
    "video",
  ];

  #appName;
  #name;
  #requiredParams = {};
  #routines = [];
  #startingParams = {};
  #params = {};

  // --------------------- //
  // Public Static Methods //
  // --------------------- //


  // ----------------------- //
  // Public Instance Methods
  // ----------------------- //

  constructor(appName, eventName, config) {
    super();
    this.#appName = appName;
    this.#assertValidConfig(eventName, config);
    this.#name = eventName;
    this.#requiredParams = config.params;
    this.#routines = config.routines;
  }

  // Runs the event's routines against the supplied event params. Deliberately takes no
  // `contexts` argument — routines must rely exclusively on event params for caller-supplied
  // values, and reach for `source: currentUser` when they need the current user. The caller
  // that built `params` was responsible for resolving anything it wanted routines to see
  // (usually an entity action extracting values from its `contexts` map).
  executeRoutines(params) {
    this.#validateRuntimeParams(params);
    const scope = effectScope();
    this.#params = params;
    this.#startingParams = JSON.parse(JSON.stringify(params));

    scope.run(() => {
      this.#routines.forEach(routineDef => {
        const routineParameterSources = routineDef.params;

        const shouldRoutineRun = Routine.evaluateShouldRoutineRun(routineDef, this.#params);
        if (!shouldRoutineRun) {
          return;
        }

        const routine = Routine.make(this.#appName, routineDef.routineType, routineParameterSources);
        try {
          this.#params[routineDef.name] = routine.execute(this.#params);
        } catch (e) {
          throw new Error(
            `Event "${this.#name}" failed at routine step "${routineDef.name}" (type: ${routineDef.routineType}): ${e.message}`,
            { cause: e },
          );
        }
      });
    });

    scope.stop();
    return this.#params;
  }

  getTransactionObject() {
    return {
      "eventType": this.#name,
      // "params": this.#startingParams, // @TODO: all we need here are the temp ids - refactor to reduce data volume
      "params": this.#params,
    };
  }

  // --------------- //
  // Private Methods //
  // --------------- //

  #validateRuntimeParams(params) {
    Object.keys(this.#requiredParams).forEach(paramName => {
      const paramDef = this.#requiredParams[paramName];

      // Optional params are allowed to be absent — the routine field's default value will be used instead.
      if (paramDef.optional) {
        return;
      }

      const value = params[paramName];
      if (value === undefined || value === null) {
        throw new Error(`event "${this.#name}" is missing required param "${paramName}"`);
      }
    });
  }

  #assertValidConfig(eventName, config) {
    this.#assertValidConfigParams(eventName, config);
    this.#assertValidConfigRoutines(eventName, config);
  }

  #assertValidConfigRoutines(eventName, config) {
    if (!config.routines) {
      throw new Error(`event "${eventName}" must define routines`);
    }
    if (!Array.isArray(config.routines)) {
      throw new Error(`event "${eventName}" routines must be an array`);
    }
    config.routines.forEach((routine, index) => {
      Routine.assertValidUse(eventName, routine, index);
    });
  }

  #assertValidConfigParams(eventName, config) {
    if (!config.params) {
      throw new Error("you must supply params");
    }
    if (config.params === null || typeof config.params !== "object" || Array.isArray(config.params)) {
      if (Array.isArray(config.params)) {
        config.params = {}; // todo: fix, backend is returning empty object as array
      } else {
        throw new Error("params must be an object");
      }
    }
    if (!Array.isArray(config.routines)) {
      throw new Error("routines must be an array");
    }
    Object.keys(config.params).forEach(paramName => {
      const paramDef = config.params[paramName];
      if (paramDef === null || typeof paramDef !== "object" || Array.isArray(paramDef)) {
        throw new Error("each param definition must be an object");
      }
      if (!paramDef.type) {
        throw new Error(`each param definition must include a type (${JSON.stringify(paramDef)})`);
      }
      if (!Event.#validParamTypes.includes(paramDef.type)) {
        throw new Error(`param definition contains unknown type: ${paramDef.type}`);
      }
      switch (paramDef.type) {
        case "entity":
          if (!paramDef.entityType) {
            throw new Error("param of type entity must also define an entityType");
          }
          break;
      }
    });
  }
}
